/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { WithDirty, WithFormMode } from '@gh/common-shared-data';
import { FormMode, isCreateFormMode, isEditableFormMode, isEditFormMode, isViewFormMode } from '@gh/core-ui';
import { merge$ } from '@gh/rx';
import { map$, mapTo$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { ContainerState } from '../container-state';
import { queryDirty, queryFormMode, setEntityField, setEntityFields } from './entity/entity.utils';
import { State } from './observable/state';

type PlainState<T> = WithFormMode<WithDirty<{ value: T; isPending: boolean }>>;

enum PlainStateEventType { SetDirty, SetPending, SetFormMode, SetValue }

type PlainStateSetDirtyEvent = { type: PlainStateEventType.SetDirty; dirty: boolean };
type PlainStateSetPendingEvent = { type: PlainStateEventType.SetPending; pending: boolean };
type PlainStateSetFormModeEvent = { type: PlainStateEventType.SetFormMode; formMode: FormMode };
type PlainStateSetValueEvent = { type: PlainStateEventType.SetValue; value: any };
type PlainStateEvent = PlainStateSetDirtyEvent | PlainStateSetPendingEvent | PlainStateSetFormModeEvent
  | PlainStateSetValueEvent;

function queryPending<T>({ isPending }: PlainState<T>): boolean {
  return isPending;
}

function queryPlainStateValue<T>(state: PlainState<T>): T {
  return state.value;
}

function createPlainStateInit<T>(initialValue: T): PlainState<T> {
  return ({ formMode: FormMode.CreateMode, isDirty: false, isPending: false, value: initialValue });
}

function plainStateReducer<T>(state: PlainState<T>, event: PlainStateEvent): PlainState<T> {
  switch (event.type) {
    case PlainStateEventType.SetFormMode:
      return setEntityField(state, 'formMode', event.formMode);
    case PlainStateEventType.SetDirty:
      return setEntityField(state, 'isDirty', event.dirty);
    case PlainStateEventType.SetPending:
      return setEntityField(state, 'isPending', event.pending);
    case PlainStateEventType.SetValue:
      return setEntityField(state, 'value', event.value);
    default:
      return state;
  }
}

function plainStateSelector<T>(state: PlainState<T>,
                               selector: (state: T, ...args: any[]) => PlainState<T>,
                               ...args: any[]): PlainState<T> {
  return selector(queryPlainStateValue(state), ...args);
}

/**
 * Simplified immutable {@link ContainerState} which has simplicity of {@link MutablePageState}
 * and purity of {@link ImmutablePageState}.
 * Purity allows this state to be used for creation of <code>OnPush</code> components.
 */
export class PlainImmutablePageState<T> implements ContainerState<T> {
  changes: Observable<void>;
  dirty$: Observable<boolean>;
  mode$: Observable<FormMode>;
  editable$: Observable<boolean>;
  createMode$: Observable<boolean>;
  viewMode$: Observable<boolean>;
  editMode$: Observable<boolean>;

  private _mode: FormMode = FormMode.CreateMode;
  private _dirty = false;
  private _changed = false;
  private state: State<PlainState<T>>;

  constructor(initialValue: T, mode: FormMode = FormMode.CreateMode) {
    const state = new State(createPlainStateInit(initialValue), plainStateReducer);
    const dirty$ = state.select(queryDirty);
    const pending$ = state.select(queryPending);
    const formMode$ = state.select(queryFormMode);

    dirty$.subscribe((dirty) => this._dirty = dirty);
    formMode$.subscribe((formMode) => this._mode = formMode);

    this.state = state;
    this._mode = mode;
    this.dirty$ = dirty$;
    this.mode$ = formMode$;
    this.editable$ = formMode$.pipe(map$(isEditableFormMode));
    this.createMode$ = formMode$.pipe(map$(isCreateFormMode));
    this.viewMode$ = formMode$.pipe(map$(isViewFormMode));
    this.editMode$ = formMode$.pipe(map$(isEditFormMode));
    this.changes = merge$(dirty$, pending$, formMode$).pipe(mapTo$(void 0));
  }

  get submitValue(): T {
    return this.state.project(queryPlainStateValue);
  }

  get dirty(): boolean {
    return this._dirty;
  }

  set mode(_: FormMode) {
    throw new Error('PlainImmutablePageState.mode setter was deprecated: use use setMode function instead');
  }

  get mode(): FormMode {
    return this._mode;
  }

  get createMode(): boolean {
    return this.mode === FormMode.CreateMode;
  }

  get editMode(): boolean {
    return this.mode === FormMode.EditMode;
  }

  get editable(): boolean {
    return this.createMode || this.editMode;
  }

  get viewMode(): boolean {
    return this.mode === FormMode.ViewMode;
  }

  get invalid(): boolean {
    return false;
  }

  get pending(): boolean {
    return this.state.project(queryPending);
  }

  get value(): T {
    return this.state.project(queryPlainStateValue);
  }

  get valueAsStream(): Observable<T> {
    return this.state.select(queryPlainStateValue);
  }

  get changed(): boolean {
    return this._changed;
  }

  build(...args: any[]): void {
    // nothing to do
  }

  /**
   * Creates stream of data returned from the underlying reactive state.
   * Data is retrieved using given selector. Selector MUST BE a pure function which accepts state of container.
   */
  select<U>(selector: (value: T) => U): Observable<U>;
  select<U, A1>(selector: (value: T, a1: A1) => U, a1: A1): Observable<U>;
  select<U, A1, A2>(selector: (value: T, a1: A1, a2: A2) => U, a1: A1, a2: A2): Observable<U>;
  select<U, A1, A2, A3>(selector: (value: T, a1: A1, a2: A2, a3: A3) => U, a1: A1, a2: A2, a3: A3): Observable<U>;
  select(selector: (value: any, ...args: any[]) => any, ...args: any[]): any {
    return this.state.select(plainStateSelector, selector, ...args);
  }

  /**
   * Calculates some value based on the underlying reactive state.
   * Data is retrieved using given selector. Selector MUST BE a pure function which accepts state of container.
   */
  project<U>(selector: (value: T) => U): U;
  project<U, A1>(selector: (value: T, a1: A1) => U, a1: A1): U;
  project<U, A1, A2>(selector: (value: T, a1: A1, a2: A2) => U, a1: A1, a2: A2): U;
  project<U, A1, A2, A3>(selector: (value: T, a1: A1, a2: A2, a3: A3) => U, a1: A1, a2: A2, a3: A3): U;
  project(selector: (value: any, ...args: any[]) => any, ...args: any[]): any {
    return this.state.project(plainStateSelector, selector, ...args);
  }

  /**
   * Sets top level field of the underlying state object
   */
  setField<N extends keyof T>(field: N, value: T[N]): void {
    this.setValue(setEntityField(this.value, field, value));
  }

  /**
   * Sets top level field of the underlying state object and mark this state as dirty if value has changed
   */
  setFieldWithDirty<N extends keyof T>(field: N, value: T[N]): void {
    this.setValueWithDirty(setEntityField(this.value, field, value));
  }

  /**
   * Sets set of top level fields of the underlying state object
   */
  setFields(fields: Hash<any>): void {
    this.setValue(setEntityFields(this.value, fields));
  }

  /**
   * Sets mode of the {@link ContainerState}
   * @param {FormMode} formMode
   */
  setMode(formMode: FormMode): void {
    this.state.mutate({ type: PlainStateEventType.SetFormMode, formMode });
  }

  /**
   * Sets underlying state of the container
   */
  setValue(newValue: T): void {
    const oldValue = this.value;
    this.state.mutate({ type: PlainStateEventType.SetValue, value: newValue });
    if (oldValue !== newValue) {
      this._changed = true;
    }
  }

  /**
   * Sets top level field of the underlying state object and mark this state as dirty if value has changed
   */
  setValueWithDirty(newValue: T): void {
    const { value: oldValue } = this;
    this.setValue(newValue);
    if (this.value !== oldValue) {
      this.setDirty();
    }
  }

  /**
   * Sets dirty flag
   */
  setDirty(dirty: boolean = true): void {
    this.state.mutate({ type: PlainStateEventType.SetDirty, dirty });
  }

  /**
   * Resets dirty flag to false
   */
  resetDirty(): void {
    this.setDirty(false);
  }

  /**
   * Simplifies tracking of changes in the underlying state.
   * Block which CAN change underlying state should begin with {@link #startChangeDetection} method.
   * Developer can use {@link #changed} getter to determine if change has been occurred.
   * <code>
   *   this.startChangeDetection();
   *   this.setField('someField', someNewValue);
   *   console.log(this.changed);
   * </code>
   *
   * The common case is marking state as dirty when some change has been occurred.
   * In this case develper casn use method {@link #setDirtyIfChanged}
   * <code>
   *   this.startChangeDetection();
   *   this.setField('someField', someNewValue);
   *   this.setDirtyIfChanged();
   * </code>
   */
  startChangeDetection(): void {
    this._changed = false;
  }

  /**
   * Stops detection of changes
   */
  endChangeDetection(): void {
    this._changed = false;
  }

  /**
   * Marks form as dirty if some change has been occurred.
   */
  setDirtyIfChanged(): void {
    if (this._changed) {
      this.setDirty();
      this.endChangeDetection();
    }
  }

  /**
   * Sets pending flag
   */
  setPending(pending: boolean): void {
    this.state.mutate({ type: PlainStateEventType.SetPending, pending });
  }
}
